import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Sparkles, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";
import { pad } from "@/lib/yasamaru";

export function ProxyInputDialog({ target, period, days, closedDates, periodId, existingRequests, onClose, onSaved }) {
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const closedSet = useMemo(() => new Set(closedDates || []), [closedDates]);

  // Reset marks whenever target changes
  useEffect(() => {
    if (!target || !period) { setMarks({}); return; }
    const initial = {};
    for (const r of existingRequests || []) {
      if (r.user_id === target.user_id) initial[r.date] = r.request_type;
    }
    setMarks(initial);
  }, [target, period, existingRequests]);

  // 集計 (Hook は早期return前に必ず呼ぶ)
  const counts = useMemo(() => {
    let a = 0, e = 0, u = 0;
    for (const dateStr in marks) {
      if (closedSet.has(dateStr)) continue;
      if (marks[dateStr] === "available") a++;
      else if (marks[dateStr] === "either") e++;
      else if (marks[dateStr] === "unavailable") u++;
    }
    return { a, e, u };
  }, [marks, closedSet]);

  if (!target || !period) return null;

  const setMark = (dateStr, v) => {
    setMarks((prev) => {
      const next = { ...prev };
      if (prev[dateStr] === v) delete next[dateStr];
      else next[dateStr] = v;
      return next;
    });
  };

  // 日付セル作成
  const cells = [];
  for (let d = 1; d <= days; d++) {
    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
    const wd = new Date(period.year, period.month - 1, d).getDay();
    cells.push({ d, wd, dateStr, isClosed: closedSet.has(dateStr) });
  }

  const fillAll = (v) => {
    const next = { ...marks };
    for (const c of cells) {
      if (!c.isClosed) next[c.dateStr] = v;
    }
    setMarks(next);
  };
  const fillWeekendUnavailable = () => {
    const next = { ...marks };
    for (const c of cells) {
      if (!c.isClosed && (c.wd === 0 || c.wd === 6)) next[c.dateStr] = "unavailable";
    }
    setMarks(next);
  };
  const clearAll = () => setMarks({});

  const runAiParse = async () => {
    if (!aiText.trim()) {
      toast.error("LINE/メモの文章を貼り付けてください");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/parse-shift-request", {
        year: period.year,
        month: period.month,
        days_in_month: days,
        text: aiText,
      });
      const next = { ...marks };
      let applied = 0;
      for (const it of data.items || []) {
        const ds = `${period.year}-${pad(period.month)}-${pad(it.day)}`;
        if (closedSet.has(ds)) continue;
        next[ds] = it.request_type;
        applied++;
      }
      setMarks(next);
      if (applied === 0) {
        toast.warning("解析できる日付が見つかりませんでした");
      } else {
        toast.success(`AI解析: ${applied}日分を反映しました${data.notes ? `（${data.notes}）` : ""}`);
        setAiOpen(false);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiBusy(false);
    }
  };

  const desiredDays = target.monthly_desired_days;

  const save = async () => {
    const items = [];
    for (const dateStr in marks) {
      if (closedSet.has(dateStr)) continue;
      const t = marks[dateStr];
      if (!t) continue;
      items.push({
        date: dateStr,
        request_type: t,
        // 時間はあえて指定しない (本人がログインしたら追記OK)
      });
    }
    if (items.length === 0) {
      toast.error("少なくとも1日は入力してください");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/shift-requests/proxy-bulk?target_user_id=${target.user_id}`, {
        shift_period_id: periodId,
        items,
      });
      toast.success(`${target.display_name || target.name} さんの希望を ${items.length}日 代理入力しました`);
      await onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const firstWd = new Date(period.year, period.month - 1, 1).getDay();

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="proxy-input-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            <span>{target.display_name || target.name} さんの希望を 代理入力</span>
          </DialogTitle>
        </DialogHeader>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
          <p>口頭で聞いた希望や、LINEで届いた連絡をここで入力できます。<br />
          各日付をタップ → 出てくる「◎ △ ×」から選択。同じ印をもう一度タップで取り消し。</p>
          {desiredDays ? (
            <p className="mt-1.5 text-blue-700">
              月の希望日数：<b>{desiredDays}日</b>　/　現在の◎：<b className={counts.a > desiredDays ? "text-rose-600" : "text-emerald-700"}>{counts.a}日</b>
            </p>
          ) : null}
        </div>

        {/* AI 自動解析パネル */}
        <div className="border-2 border-dashed border-purple-200 rounded-lg p-3 bg-purple-50/50">
          <button
            type="button"
            onClick={() => setAiOpen(!aiOpen)}
            className="flex items-center justify-between w-full text-left"
            data-testid="proxy-ai-toggle"
          >
            <span className="flex items-center gap-2 font-bold text-sm text-purple-800">
              <Sparkles className="w-4 h-4 text-purple-600" />
              AIで自動解析 (LINE/メモを貼り付けるだけ)
            </span>
            <ChevronRight className={`w-4 h-4 text-purple-600 transition-transform ${aiOpen ? "rotate-90" : ""}`} />
          </button>
          {aiOpen && (
            <div className="mt-3 space-y-2">
              <textarea
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                rows={4}
                placeholder="例: 6/3、5、10、15は入れます。土日はお休みでお願いします。20-22は家族旅行のためNG。それ以外の平日は時間があれば入れます"
                className="w-full text-xs border border-purple-200 rounded-lg p-2.5 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-purple-300"
                data-testid="proxy-ai-text"
                disabled={aiBusy}
              />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[10px] text-purple-700">※ AI が日付と「入れる/入れない」を自動でカレンダーに反映します。すでに入力済みの日は上書きされます。</p>
                <Button
                  size="sm"
                  onClick={runAiParse}
                  disabled={aiBusy || !aiText.trim()}
                  className="h-8 text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-60"
                  data-testid="proxy-ai-run"
                >
                  {aiBusy ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin mr-1.5" />
                      解析中…
                    </>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1" />解析する</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 一括操作 */}
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700" onClick={() => fillAll("available")} data-testid="proxy-fill-available">
            <Check className="w-3 h-3 mr-1" />全日「◎入れます」
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-700" onClick={() => fillAll("either")} data-testid="proxy-fill-either">
            全日「△必要なら」
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" onClick={fillWeekendUnavailable} data-testid="proxy-fill-weekend">
            土日「×入れません」
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 ml-auto" onClick={clearAll} data-testid="proxy-clear">
            全てクリア
          </Button>
        </div>

        {/* カレンダー */}
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
              <div key={w} className={`text-center text-xs font-bold py-1 ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-slate-600"}`}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstWd }, (_, i) => <div key={`b${i}`} />)}
            {cells.map((c) => (
              <ProxyDayCell key={c.dateStr} day={c.d} wd={c.wd} isClosed={c.isClosed} value={marks[c.dateStr]} onChange={(v) => setMark(c.dateStr, v)} />
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>◎ <b className="text-emerald-700">{counts.a}</b>日</span>
            <span>△ <b className="text-amber-700">{counts.e}</b>日</span>
            <span>× <b className="text-rose-700">{counts.u}</b>日</span>
            <span className="text-slate-400 ml-auto">※ 時間は指定なし。後で本人が追記できます。</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="proxy-cancel">キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="proxy-save">
            {saving ? "保存中..." : "希望として保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProxyDayCell({ day, wd, isClosed, value, onChange }) {
  if (isClosed) {
    return (
      <div className="min-h-[60px] rounded border border-slate-200 bg-slate-100 flex flex-col items-center justify-center text-[10px] text-slate-400">
        <span className="font-bold text-sm">{day}</span>
        <span>休</span>
      </div>
    );
  }
  const opts = [
    { v: "available", label: "◎", tone: value === "available" ? "bg-emerald-500 text-white" : "text-emerald-700 hover:bg-emerald-50" },
    { v: "either", label: "△", tone: value === "either" ? "bg-amber-500 text-white" : "text-amber-700 hover:bg-amber-50" },
    { v: "unavailable", label: "×", tone: value === "unavailable" ? "bg-rose-500 text-white" : "text-rose-700 hover:bg-rose-50" },
  ];
  const ring = value === "available" ? "ring-2 ring-emerald-400" : value === "either" ? "ring-2 ring-amber-400" : value === "unavailable" ? "ring-2 ring-rose-400" : "";
  return (
    <div className={`min-h-[60px] rounded border border-slate-200 bg-white p-0.5 flex flex-col ${ring}`} data-testid={`proxy-day-${day}`}>
      <span className={`text-[10px] font-bold text-center ${wd === 0 ? "text-red-500" : wd === 6 ? "text-blue-500" : "text-slate-600"}`}>{day}</span>
      <div className="grid grid-cols-3 gap-px flex-1">
        {opts.map((o) => (
          <button
            key={o.v} type="button"
            onClick={() => onChange(o.v)}
            data-testid={`proxy-day-${day}-${o.v}`}
            className={`text-[11px] font-bold rounded-sm transition-colors ${o.tone}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
